import React, { useState, useEffect, useRef } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { NavDropdown } from 'react-bootstrap';
import * as settings from "../settings";
import * as functions from "../functions";

/**
 * FloatingNavbar
 *
 * Primary navbar  ➜  logo  |  search  |  main links
 * Secondary navbar ➜  filter chips & “Save search” button
 *
 * The <header> wrapper is `fixed-top`; its total height (primary + secondary
 * rows) is measured with a ref and reported upward via `setNavbarHeight` so
 * the calling page can insert an equivalent spacer and avoid jumpy layout.
 */
const FloatingNavbar = ({ navbarHeight, setNavbarHeight, isMobile }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navbarRef = useRef(null);
  const [articles, setArticles] = useState([]);

  const handleToggle = () => setIsOpen((prev) => !prev);
  const handleClose = () => setIsOpen(false);

  // Measure full header height (both navbars)
  useEffect(() => {
    if (!navbarRef.current) return;
    const updateHeight = () => setNavbarHeight(navbarRef.current.offsetHeight);
    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, [setNavbarHeight]);

  // Fetch articles for dropdown
  useEffect(() => {
    fetch('/articles.json')
      .then(response => response.json())
      .then(data => setArticles(data))
      .catch(error => console.error('Error fetching articles:', error));
  }, []);

  const clientArticles = articles.filter(article => article.targetAudience === "Client").slice(0, 4);
  const attorneyArticles = articles.filter(article => article.targetAudience === "Attorney").slice(0, 4);

  // Helper helper to wrap text if needed
  const getItemStyle = (title) => {
    if (isMobile && title.length > 44) {
      return { whiteSpace: 'normal', maxWidth: '300px' };
    }
    return {};
  };

  return (
    <>
      {/* ============================  FIXED HEADER  ============================ */}
      <header ref={navbarRef} className="fixed-top bg-white shadow-sm">
        {/* -----------------------  PRIMARY NAVBAR  ----------------------- */}
        <nav className="navbar navbar-expand-lg navbar-light bg-light">
          <div className="container-fluid">
            {/* Logo */}
            <a className="navbar-brand d-flex align-items-center" href="/" onClick={handleClose}>
              <img src="/logo.svg" alt="Logo" style={{ height: "45px" }} className="me-2" />
            </a>

            {/* Hamburger menu */}
            <button
              className="navbar-toggler"
              type="button"
              aria-controls="navbarNav"
              aria-expanded={isOpen}
              aria-label="Toggle navigation"
              onClick={handleToggle}
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            {/* Nav links */}
            <div className={`collapse navbar-collapse ${isOpen ? "show" : ""}`} id="navbarNav">
              <ul className="navbar-nav ms-auto text-end mt-2 mt-lg-0">
                <li className="nav-item">
                  <a className="nav-link text-wrap text-lg-nowrap" href="/" onClick={handleClose}>Home</a>
                </li>
                <li className="nav-item">
                  {/* Absolute hash paths so they work under any subroute */}
                  <a className="nav-link text-wrap text-lg-nowrap" href="/agents" onClick={handleClose}>Our Agents</a>
                </li>
                <li className="nav-item">
                  <a className="nav-link text-wrap text-lg-nowrap" href="/property-management" onClick={handleClose}>Property Management</a>
                </li>

                <NavDropdown title="Articles" id="article-nav-dropdown" align="end">


                  <NavDropdown.Header>For Clients</NavDropdown.Header>
                  {clientArticles.map((article, index) => (
                    <NavDropdown.Item
                      key={article.id}
                      href={`/article/${article.slug}`}
                      onClick={handleClose}
                      className={index % 2 !== 0 ? "bg-stripe-odd" : ""}
                      style={getItemStyle(article.title)}
                    >
                      {article.title}
                    </NavDropdown.Item>
                  ))}

                  <NavDropdown.Divider />

                  <NavDropdown.Header>For Attorneys</NavDropdown.Header>
                  {attorneyArticles.map((article, index) => (
                    <NavDropdown.Item
                      key={article.id}
                      href={`/article/${article.slug}`}
                      onClick={handleClose}
                      className={index % 2 !== 0 ? "bg-stripe-odd" : ""}
                      style={getItemStyle(article.title)}
                    >
                      {article.title}
                    </NavDropdown.Item>
                  ))}
                  <NavDropdown.Divider />
                  <NavDropdown.Item href="/article" onClick={handleClose}>View All Articles ({articles.length})</NavDropdown.Item>

                </NavDropdown>

                <li className="nav-item">
                  <a className="nav-link text-wrap text-lg-nowrap" href="/#about" onClick={handleClose}>About Us</a>
                </li>
                <li className="nav-item">
                  {/* Absolute hash paths so they work under any subroute */}
                  <a className="nav-link text-wrap text-lg-nowrap" href="/step/100" onClick={handleClose}>Buy</a>
                </li>
                <li className="nav-item">
                  {/* Absolute hash paths so they work under any subroute */}
                  <a className="nav-link text-wrap text-lg-nowrap" href="/step/150" onClick={handleClose}>Sell</a>
                </li>
                <li className="nav-item">
                  <a
                    className="nav-link text-wrap text-lg-nowrap"
                    href="https://portal.pembre.com"
                    target="_blank"
                    onClick={handleClose}
                  >
                    Portal Login
                  </a>
                </li>

              </ul>
            </div>

            {/* Phone number (visible on desktop) */}
            <div className="d-none d-lg-block ms-3">
              <a
                href={`tel:${settings.COMPANYTELPHONE}`}
                className="navbar-text text-nowrap"
                onClick={() => functions.triggerPhoneConversion()}
              >
                {settings.COMPANYFRIENDLYPHONE}
              </a>
            </div>
          </div>
        </nav>
      </header>

      {/* Spacer to offset the fixed header height */}
      <div style={{ height: `${navbarHeight}px` }} />
    </>
  );
};

export default FloatingNavbar;